import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  ChefHat, Loader2, CheckCircle2, AlertCircle, KeyRound,
  Lock, Eye, EyeOff, User,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

// ─── 6-digit OTP input ────────────────────────────────────────────────
function OtpInput({ value, onChange, disabled }) {
  const DIGITS = 6;
  const refs = Array.from({ length: DIGITS }, () => useRef(null)); // eslint-disable-line

  const digits = value.split("").concat(Array(DIGITS).fill("")).slice(0, DIGITS);

  const handleKey = (i, e) => {
    if (e.key === "Backspace") {
      const next = value.slice(0, i) + value.slice(i + 1);
      onChange(next);
      if (i > 0) refs[i - 1].current?.focus();
      return;
    }
    if (!/^\d$/.test(e.key)) return;
    const next = (value.slice(0, i) + e.key + value.slice(i + 1)).slice(0, DIGITS);
    onChange(next);
    if (i < DIGITS - 1) refs[i + 1].current?.focus();
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, DIGITS);
    if (paste) {
      onChange(paste);
      refs[Math.min(paste.length, DIGITS - 1)].current?.focus();
    }
    e.preventDefault();
  };

  return (
    <div className="flex gap-2 justify-center" data-testid="otp-input-group">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={refs[i]}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          onChange={() => {}}
          onKeyDown={(e) => handleKey(i, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          disabled={disabled}
          data-testid={`otp-digit-${i}`}
          className={`w-12 h-13 text-center text-2xl font-bold rounded-xl border-2 bg-transparent
            focus:outline-none transition-colors
            ${d
              ? "border-orange-500 text-foreground"
              : "border-muted-foreground/30 text-muted-foreground"
            }
            ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
          style={{ height: "52px" }}
        />
      ))}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────
export default function PlatformInvitationPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [invitation, setInvitation] = useState(null);
  const [loadingInv, setLoadingInv] = useState(true);
  const [invError, setInvError] = useState(null);

  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  // Load invitation info
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/join/${token}`
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || "no_encontrada");
        }
        const data = await res.json();
        setInvitation(data);
        if (data.name) setName(data.name);
      } catch (e) {
        setInvError(e.message);
      } finally {
        setLoadingInv(false);
      }
    };
    load();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (otp.length < 6) { toast.error("Introduce el código completo de 6 dígitos"); return; }
    if (!name.trim()) { toast.error("El nombre es obligatorio"); return; }
    if (password.length < 6) { toast.error("La contraseña debe tener al menos 6 caracteres"); return; }

    setSubmitting(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/join/${token}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ otp: otp.trim(), password, name: name.trim() }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error al verificar el código");

      // Store token for mobile browsers that block cookies
      if (data.access_token) {
        api.setToken(data.access_token);
      }

      setAccepted(true);
      toast.success("¡Cuenta creada! Bienvenido a MenuGenius");
    } catch (err) {
      toast.error(err.message);
      setOtp("");
    } finally {
      setSubmitting(false);
    }
  };

  const strengthScore = password.length === 0 ? 0
    : password.length < 6 ? 1
    : password.length < 10 ? 2
    : password.length < 14 ? 3
    : 4;

  // ── Loading ──────────────────────────────────────────────────────────
  if (loadingInv) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────
  if (invError) {
    const messages = {
      expirada:     "Este enlace ha expirado (48 h). Pide al administrador que reenvíe la invitación.",
      ya_usada:     "Esta invitación ya fue utilizada. Si ya tienes cuenta, inicia sesión.",
      revocada:     "Esta invitación ha sido revocada. Contacta al administrador.",
      no_encontrada: "Este enlace no es válido.",
    };
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md text-center">
          <div className="inline-flex items-center gap-2 mb-6">
            <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center">
              <ChefHat className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-bold text-lg">MenuGenius</span>
          </div>
          <div className="bg-card border rounded-xl p-8">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Invitación no válida</h2>
            <p className="text-muted-foreground text-sm mb-6">
              {messages[invError] || messages.no_encontrada}
            </p>
            <Button asChild variant="outline">
              <Link to="/login">Ir al inicio de sesión</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Success ───────────────────────────────────────────────────────────
  if (accepted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md text-center">
          <div className="inline-flex items-center gap-2 mb-6">
            <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center">
              <ChefHat className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-bold text-lg">MenuGenius</span>
          </div>
          <div className="bg-card border rounded-xl p-8">
            <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30
                            flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-9 h-9 text-emerald-700" />
            </div>
            <h2 className="text-xl font-semibold mb-2">¡Bienvenido a MenuGenius!</h2>
            <p className="text-muted-foreground text-sm mb-6">
              Tu cuenta ha sido creada. Ya puedes empezar a gestionar tu restaurante.
            </p>
            <Button
              onClick={() => navigate("/onboarding")}
              data-testid="btn-go-to-app"
              className="w-full"
            >
              Empezar ahora
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main form ─────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br
                    from-background to-muted/30 p-4">
      <div className="w-full max-w-md space-y-5">

        {/* Brand */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2.5 mb-2">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center shadow-lg">
              <ChefHat className="w-6 h-6 text-primary-foreground" />
            </div>
            <span className="font-bold text-xl">MenuGenius</span>
          </div>
          <p className="text-muted-foreground text-sm">Activa tu cuenta</p>
        </div>

        {/* Info card */}
        <div className="bg-card border rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <KeyRound className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium">Has sido invitado a MenuGenius</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Cuenta para: <strong>{invitation?.email}</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Form card */}
        <div className="bg-card border rounded-xl p-6">
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="join-form">

            {/* OTP */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Código de acceso *
              </Label>
              <OtpInput value={otp} onChange={setOtp} disabled={submitting} />
              <p className="text-xs text-center text-muted-foreground">
                Revisa tu correo en <strong>{invitation?.email}</strong>
              </p>
            </div>

            {/* Name */}
            <div>
              <Label htmlFor="join-name" className="text-sm font-medium">
                Tu nombre completo *
              </Label>
              <div className="relative mt-1.5">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="join-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nombre completo"
                  required
                  className="pl-9"
                  data-testid="join-name-input"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <Label htmlFor="join-pass" className="text-sm font-medium">
                Elige una contraseña *
              </Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="join-pass"
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="pl-9 pr-10"
                  data-testid="join-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPass((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {/* Strength bar */}
              {password && (
                <div className="mt-1.5 flex gap-1">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className={`h-1 flex-1 rounded-full transition-colors ${
                        i <= strengthScore
                          ? strengthScore <= 1 ? "bg-red-400"
                            : strengthScore <= 2 ? "bg-amber-400"
                            : "bg-emerald-500"
                          : "bg-muted"
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={submitting || otp.length < 6 || !name.trim() || password.length < 6}
              data-testid="join-submit-btn"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creando cuenta...</>
              ) : (
                <><Lock className="w-4 h-4 mr-2" />Activar mi cuenta</>
              )}
            </Button>

          </form>
        </div>

        <p className="text-center text-xs text-muted-foreground">
          ¿Ya tienes cuenta?{" "}
          <Link to="/login" className="text-primary underline">
            Inicia sesión
          </Link>
        </p>
      </div>
    </div>
  );
}
